// frontend/src/pages/CheckoutPage.jsx
// Checkout page to collect user details before payment

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, Shield } from 'lucide-react';
import { coursesAPI, paymentAPI } from '../utils/api';
import { formatCurrency, isValidPhone } from '../lib/utils';

const loadRazorpay = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();
  const script = document.createElement('script'); script.src = 'https://checkout.razorpay.com/v1/checkout.js'; script.async = true;
  script.onload = resolve; script.onerror = () => reject(new Error('Secure payment window could not be loaded.'));
  document.head.appendChild(script);
});

export const buildCourseCheckoutPayload = (courseId, formData) => ({
  product_type: 'course',
  product_id: courseId,
  coupon_code: formData.couponCode.trim().toUpperCase() || undefined,
  billing: {
    name: `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim(),
    email: formData.email.trim(),
    phone: formData.phone.replace(/[^\d+ -]/g, ''),
    address_line1: formData.address.trim(),
    city: formData.city.trim(),
    state: formData.state.trim(),
    state_code: formData.stateCode,
    postal_code: formData.pincode,
    gstin: formData.gstin.trim().toUpperCase() || undefined,
  },
});

const CheckoutPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    stateCode: '',
    pincode: '',
    gstin: '',
    couponCode: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCourse();
    if (user) {
      prefillUserData();
    }
  }, [slug, user]);

  const fetchCourse = async () => {
    try {
      const response = await coursesAPI.getBySlug(slug);
      setCourse(response.data.data);
    } catch (error) {
      console.error('Error fetching course:', error);
      alert('Course not found');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  const prefillUserData = () => {
    setFormData(prev => ({
      ...prev,
      firstName: user.name?.split(' ')[0] || '',
      lastName: user.name?.split(' ').slice(1).join(' ') || '',
      email: user.email || '',
      phone: user.profile?.phone_number || '',
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhone(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }
    if (!/^\d{2}$/.test(formData.stateCode)) newErrors.stateCode = 'Enter the 2-digit GST state code';
    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Please enter a valid 6-digit pincode';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const openRazorpay = async (checkout) => {
    await loadRazorpay();
    await new Promise((resolve, reject) => {
      const instance = new window.Razorpay({
        key: checkout.key_id, amount: checkout.amount, currency: checkout.currency, order_id: checkout.gateway_order_id,
        name: 'iStockly', description: course.title,
        prefill: { name: `${formData.firstName} ${formData.lastName}`.trim(), email: formData.email, contact: formData.phone },
        modal: { ondismiss: () => reject(new Error('Payment was cancelled. Your order remains unpaid.')) },
        handler: async (result) => {
          try {
            await paymentAPI.verify({ payment_attempt_id: checkout.payment_attempt_id, gateway_payment_id: result.razorpay_payment_id, signature: result.razorpay_signature });
            navigate(`/payment/success?order=${checkout.order_id}`); resolve();
          } catch (error) { reject(error); }
        },
      });
      instance.on('payment.failed', (event) => reject(new Error(event.error?.description || 'Payment failed.')));
      instance.open();
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      alert('Please fill in all required fields correctly');
      return;
    }

    try {
      setProcessing(true);
      const response = await paymentAPI.initiate(buildCourseCheckoutPayload(course.id, formData));
      const checkout = response.data.data;
      if (checkout.status === 'paid') { navigate(`/payment/success?order=${checkout.order_id}`); return; }
      await openRazorpay(checkout);
    } catch (error) {
      console.error('Payment error:', error);
      const message = error.response?.data?.message || Object.values(error.response?.data?.errors || {})[0]?.[0] || error.message;
      setErrors(prev => ({ ...prev, submit: message || 'Payment initiation failed. Please try again.' }));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black pt-16">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  if (!course) {
    return null;
  }

  const finalPrice = course.price?.sale_amount ?? course.price?.list_amount ?? 0;
  const listPrice = course.price?.list_amount ?? 0;
  const hasDiscount = finalPrice < listPrice;

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-gray-400 hover:text-yellow-400 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Course</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-3xl font-bold text-white mb-6">Checkout</h1>

              <form onSubmit={handleSubmit} className="space-y-6">
                {errors.submit && <p role="alert" className="rounded border border-red-500 bg-red-950/50 p-3 text-red-200">{errors.submit}</p>}
                {/* Personal Information */}
                <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                  <h2 className="text-xl font-semibold text-white mb-4">Personal Information</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-black border rounded-lg text-white focus:outline-none focus:border-yellow-500 ${
                          errors.firstName ? 'border-red-500' : 'border-zinc-800'
                        }`}
                      />
                      {errors.firstName && (
                        <p className="text-red-400 text-sm mt-1">{errors.firstName}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-black border rounded-lg text-white focus:outline-none focus:border-yellow-500 ${
                          errors.lastName ? 'border-red-500' : 'border-zinc-800'
                        }`}
                      />
                      {errors.lastName && (
                        <p className="text-red-400 text-sm mt-1">{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-black border rounded-lg text-white focus:outline-none focus:border-yellow-500 ${
                        errors.email ? 'border-red-500' : 'border-zinc-800'
                      }`}
                    />
                    {errors.email && (
                      <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="10-digit mobile number"
                      className={`w-full px-4 py-3 bg-black border rounded-lg text-white focus:outline-none focus:border-yellow-500 ${
                        errors.phone ? 'border-red-500' : 'border-zinc-800'
                      }`}
                    />
                    {errors.phone && (
                      <p className="text-red-400 text-sm mt-1">{errors.phone}</p>
                    )}
                  </div>
                </div>

                {/* Billing Address */}
                <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                  <h2 className="text-xl font-semibold text-white mb-4">Billing Address</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Address *
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="House No., Street Name"
                        className={`w-full px-4 py-3 bg-black border rounded-lg text-white focus:outline-none focus:border-yellow-500 ${
                          errors.address ? 'border-red-500' : 'border-zinc-800'
                        }`}
                      />
                      {errors.address && (
                        <p className="text-red-400 text-sm mt-1">{errors.address}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          City *
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 bg-black border rounded-lg text-white focus:outline-none focus:border-yellow-500 ${
                            errors.city ? 'border-red-500' : 'border-zinc-800'
                          }`}
                        />
                        {errors.city && (
                          <p className="text-red-400 text-sm mt-1">{errors.city}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          State *
                        </label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 bg-black border rounded-lg text-white focus:outline-none focus:border-yellow-500 ${
                            errors.state ? 'border-red-500' : 'border-zinc-800'
                          }`}
                        />
                        {errors.state && (
                          <p className="text-red-400 text-sm mt-1">{errors.state}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Pincode *
                      </label>
                      <input
                        type="text"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleInputChange}
                        placeholder="6-digit pincode"
                        maxLength="6"
                        className={`w-full px-4 py-3 bg-black border rounded-lg text-white focus:outline-none focus:border-yellow-500 ${
                          errors.pincode ? 'border-red-500' : 'border-zinc-800'
                        }`}
                      />
                      {errors.pincode && (
                        <p className="text-red-400 text-sm mt-1">{errors.pincode}</p>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="block text-sm font-medium text-gray-300">GST State Code *<input type="text" name="stateCode" value={formData.stateCode} onChange={handleInputChange} maxLength="2" inputMode="numeric" className={`mt-2 w-full px-4 py-3 bg-black border rounded-lg text-white ${errors.stateCode ? 'border-red-500' : 'border-zinc-800'}`} />{errors.stateCode && <span className="block text-red-400 text-sm mt-1">{errors.stateCode}</span>}</label>
                      <label className="block text-sm font-medium text-gray-300">GSTIN (optional)<input type="text" name="gstin" value={formData.gstin} onChange={handleInputChange} maxLength="15" className="mt-2 w-full px-4 py-3 bg-black border border-zinc-800 rounded-lg text-white uppercase" /></label>
                    </div>
                    <label className="block text-sm font-medium text-gray-300">Coupon code (optional)<input type="text" name="couponCode" value={formData.couponCode} onChange={handleInputChange} maxLength="64" className="mt-2 w-full px-4 py-3 bg-black border border-zinc-800 rounded-lg text-white uppercase" /></label>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={processing}
                  className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-yellow-500 text-black rounded-lg font-semibold hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CreditCard className="w-5 h-5" />
                  <span>
                    {processing
                      ? 'Processing...'
                      : finalPrice === 0
                        ? 'Get Free Access'
                        : 'Proceed to Payment'}
                  </span>
                </button>
              </form>
            </motion.div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="sticky top-24"
            >
              <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                <h2 className="text-xl font-semibold text-white mb-4">Order Summary</h2>

                {course.thumbnail_url && (
                  <img
                    src={course.thumbnail_url}
                    alt={course.title}
                    className="w-full aspect-video object-cover rounded-lg mb-4"
                  />
                )}

                <h3 className="font-semibold text-white mb-2">{course.title}</h3>
                <p className="text-sm text-gray-400 mb-4">{course.instructors?.map(item => item.name).join(', ')}</p>

                <div className="border-t border-zinc-800 pt-4 space-y-3">
                  <div className="flex justify-between text-gray-300">
                    <span>Original Price</span>
                    <span>{formatCurrency(listPrice / 100)}</span>
                  </div>
                  
                  {hasDiscount && (
                    <div className="flex justify-between text-green-400">
                      <span>Discount</span>
                      <span>- {formatCurrency((listPrice - finalPrice) / 100)}</span>
                    </div>
                  )}

                  <div className="border-t border-zinc-800 pt-3 flex justify-between text-white text-xl font-bold">
                    <span>Course subtotal</span>
                    <span className="flex items-center">
                      <span className="text-yellow-400">{formatCurrency(finalPrice / 100)}</span>
                    </span>
                  </div>
                </div>

                <p className="mt-4 text-xs leading-relaxed text-gray-400">Coupons and applicable GST are calculated by the server before the payment window opens. The Razorpay amount is the authoritative final total.</p>

                <div className="mt-6 p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center space-x-2 text-green-400 mb-2">
                    <Shield className="w-5 h-5" />
                    <span className="font-medium">Secure Checkout</span>
                  </div>
                  <p className="text-sm text-gray-400">
                    Your payment information is encrypted and secure
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
